/** Student availability detail tab. */
import * as React from 'react';
import { CalendarDays, GraduationCap } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailAvailabilityGrid } from '@/components/common/details/DetailAvailabilityGrid';
import {
  STUDENT_PROGRAM_CONFIG,
  STUDENT_AVAILABILITY_BOOL_LABELS,
  REMOTE_SUBJECT_CONFIG,
  COURSE_PATH_TYPE_CONFIG,
  YES_NO_BADGE_CONFIG,
  type CoursePathType,
} from '@/components/students/student-configs';
import {
  getProgramLabel,
  normalizeDetailWeeklyAvailabilitySlots,
} from '@/components/students/remote/student-table-utils';

/** Props for {@link StudentAvailabilityTab}. */
interface StudentAvailabilityTabProps {
  sections: Record<string, unknown>;
  programs: any[];
}

/** Student availability detail tab. */
export const StudentAvailabilityTab: React.FC<StudentAvailabilityTabProps> = ({
  sections,
  programs,
}) => {
  const md = sections['matching_data'] as any;

  if (!md) {
    return (
      <div className="details-view-grid mt-6">
        <DetailsCard
          icon={CalendarDays}
          title="Créneaux et options"
          colorName="color-3"
          colSpan={2}
        >
          <p className="text-sm text-muted-foreground">Disponibilités non renseignées.</p>
        </DetailsCard>
      </div>
    );
  }

  const validSlots = normalizeDetailWeeklyAvailabilitySlots(md.availability);

  const coursePathConfig = COURSE_PATH_TYPE_CONFIG[md.coursePath as CoursePathType];

  return (
    <div className="details-view-grid mt-6">
      <DetailsCard icon={CalendarDays} title="Créneaux et options" colorName="color-3" colSpan={2}>
        <div className="space-y-3">
          <DetailField
            label="Matière"
            value={
              md?.subject ? (
                <LabelBadge
                  label={REMOTE_SUBJECT_CONFIG[md.subject]?.label ?? md.subject}
                  icon={REMOTE_SUBJECT_CONFIG[md.subject]?.icon}
                  variant={REMOTE_SUBJECT_CONFIG[md.subject]?.badgeVariant ?? 'outlined'}
                  color={REMOTE_SUBJECT_CONFIG[md.subject]?.badgeColor}
                />
              ) : (
                '—'
              )
            }
          />
          <DetailField
            label="Parcours"
            value={
              md.coursePath ? (
                <LabelBadge
                  label={coursePathConfig?.label ?? md.coursePath}
                  icon={coursePathConfig?.icon ?? GraduationCap}
                  variant="outlined"
                  size="sm"
                />
              ) : (
                '—'
              )
            }
          />
          <DetailField
            label="Programme(s)"
            value={
              Array.isArray(md.programs) && md.programs.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {md.programs.map((p: any, i: number) => {
                    const programId =
                      typeof p === 'string' ? p : (p?.programId ?? p?.id ?? String(i));
                    const cfg = STUDENT_PROGRAM_CONFIG[programId];
                    const label =
                      cfg?.label ??
                      p?.programName ??
                      p?.name ??
                      p?.abbreviation ??
                      (typeof p === 'string' ? getProgramLabel(p, programs) : '?');
                    return (
                      <LabelBadge
                        key={String(p?.programId ?? p?.id ?? i)}
                        label={label}
                        icon={cfg?.icon ?? GraduationCap}
                        variant={cfg?.badgeVariant ?? 'outlined'}
                        color={cfg?.badgeColor ?? 'var(--color-1-nuance-2)'}
                        size="sm"
                      />
                    );
                  })}
                </div>
              ) : (
                '—'
              )
            }
          />
          <DetailField
            label="Créneaux hebdomadaires"
            value={
              validSlots.length > 0 ? (
                <DetailAvailabilityGrid slots={validSlots} variant="compact" />
              ) : (
                '—'
              )
            }
          />
          {(['availableOnVacations', 'availableOnWeekends', 'availableOnHolidays'] as const).map(
            (key) => {
              const boolKey = md[key] ? 'yes' : 'no';
              const cfg = YES_NO_BADGE_CONFIG[boolKey];
              return (
                <DetailField
                  key={key}
                  label={STUDENT_AVAILABILITY_BOOL_LABELS[key]}
                  value={
                    <LabelBadge
                      label={cfg.label}
                      icon={cfg.icon}
                      variant="outlined"
                      color={cfg.color}
                      size="sm"
                    />
                  }
                />
              );
            },
          )}
          {md.comments && <DetailField label="Commentaires" value={md.comments} />}
        </div>
      </DetailsCard>
    </div>
  );
};
